import * as React from 'react';
import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import {
  loadTheme,
  GDSThemeProvider,
  Spinner,
} from '@leagueplatform/genesis-core';
import { leagueTheme } from '@web-config-app/common';
import { useOnlineStatus } from '@leagueplatform/web-common';
import { LeagueProvider } from '@leagueplatform/core';
import {
  ErrorBoundaryFullPageWrapper,
  ErrorPage,
  MaintenancePage,
} from '@web-config-app/core-react-ui';
import { ConfigUserFeedbackProvider } from '@web-config-app/core-react';
import { isTenantUnderMaintenance, SIGN_OUT_PATH } from '@web-config-app/core';
import AppRoutes from './components/app-routes.component';
import { globalStyles } from './global-styles';
import { useResolvedTenant } from './hooks/use-resolved-tenant/use-resolved-tenant';
import { RootHandler } from './components/root-handler/root-handler.component';
import { AuthViewLogout } from './pages/auth/Auth.pages';

const coreTheme = loadTheme(leagueTheme, { useRems: true });
const queryClient = new QueryClient();

export function AppContent() {
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const {
    tenantId,
    setTenantId,
    config: tenantConfig,
    activeConfig,
  } = useResolvedTenant();

  globalStyles();

  let content: React.ReactNode = <ErrorPage />;

  if (isOnline) {
    if (isTenantUnderMaintenance(tenantId)) {
      content = (
        <MaintenancePage
          tenantId={tenantId}
          onSwitchWorkspace={() => {
            setTenantId('');
            navigate('/');
          }}
        />
      );
    } else {
      // During the discovery phase (no real tenant config yet), only render
      // RootHandler which handles Auth0 login and tenant resolution. The full
      // AppRoutes require configApp in LeagueProvider which discoveryConfig
      // doesn't have — rendering them would crash ValidateRouteParams.
      const isDiscoveryPhase = !tenantConfig;
      content = (
        <LeagueProvider config={activeConfig}>
          <ErrorBoundaryFullPageWrapper>
            {isDiscoveryPhase ? (
              <Routes>
                <Route path={SIGN_OUT_PATH} element={<AuthViewLogout />} />
                <Route path="*" element={<RootHandler />} />
              </Routes>
            ) : (
              <ConfigUserFeedbackProvider>
                <AppRoutes />
              </ConfigUserFeedbackProvider>
            )}
            <ReactQueryDevtools />
          </ErrorBoundaryFullPageWrapper>
        </LeagueProvider>
      );
    }
  }

  return content;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GDSThemeProvider theme={coreTheme}>
        <ErrorBoundaryFullPageWrapper>
          <Suspense fallback={<Spinner loading loadingText="Loading..." />}>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </Suspense>
        </ErrorBoundaryFullPageWrapper>
      </GDSThemeProvider>
    </QueryClientProvider>
  );
}
